import { useQuery } from '@tanstack/react-query';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import app from '@/lib/firebase';

const db = getFirestore(app);

export interface TenantBranding {
  logo_svg: string | null;
  primary_color: string;
  company_name: string;
  address: string | null;
  phone: string | null;
  email: string | null;
  payment_instructions: string | null;
}

/**
 * Get tenant ID from Firebase Auth JWT token
 */
async function getTenantId(): Promise<string | null> {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) {
    return null;
  }

  // Get ID token result which includes custom claims
  const tokenResult = await user.getIdTokenResult();
  return (tokenResult.claims.tenant_id as string) || null;
}

/**
 * Fetch tenant branding from Firestore
 * Used to populate default external_notes (payment instructions) on invoices
 */
export function useTenantBranding() {
  return useQuery<TenantBranding>({
    queryKey: ['tenant-branding'],
    queryFn: async () => {
      const tenantId = await getTenantId();

      if (!tenantId) {
        throw new Error('No tenant ID available');
      }

      const brandingRef = doc(db, `tenants/${tenantId}/settings/branding`);
      const brandingSnap = await getDoc(brandingRef);

      if (!brandingSnap.exists()) {
        // Return default branding if not configured
        return {
          logo_svg: null,
          primary_color: '#65339a',
          company_name: 'BuildGravity',
          address: null,
          phone: null,
          email: null,
          payment_instructions: null,
        };
      }

      return brandingSnap.data() as TenantBranding;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes - branding changes infrequently
  });
}
